import { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useField } from 'formik';

import Grid from '@mui/material/Grid';

import TextField from 'src/components/ui/TextField';
import Select from 'src/components/ui/Select';
import {
	joinSiteUrl,
	SITE_PROTOCOL_OPTIONS,
	SITE_PROTOCOLS,
	splitSiteUrl,
} from 'src/utils/helpers/siteUrl';

const PROTOCOL_WIDTH = 120;

const SiteUrlField = (props) => {
	const { name, placeholder = 'example.com' } = props;
	const [field, meta, helpers] = useField(name);
	const { setValue, setTouched } = helpers;

	const derived = useMemo(
		() => splitSiteUrl(field.value),
		[field.value],
	);
	const [protocol, setProtocol] = useState(derived.protocol);

	useEffect(() => {
		if (field.value) {
			setProtocol(splitSiteUrl(field.value).protocol);
		}
	}, [field.value]);

	const host = derived.host;

	const error =
		(meta.touched || meta.initialTouched) && meta.error
			? meta.error
			: undefined;

	const commit = useCallback(
		(nextProtocol, nextHost) => {
			setProtocol(nextProtocol);
			setValue(joinSiteUrl(nextProtocol, nextHost));
			setTouched(true, false);
		},
		[setTouched, setValue],
	);

	const handleProtocolChange = useCallback(
		(event) => {
			const nextProtocol = event.target.value;
			setProtocol(nextProtocol);

			if (host) {
				setValue(joinSiteUrl(nextProtocol, host));
			}

			setTouched(true, false);
		},
		[host, setTouched, setValue],
	);

	const handleHostChange = useCallback(
		(event) => {
			const raw = event?.target?.value ?? event ?? '';

			if (/^https?:\/\//i.test(String(raw).trim())) {
				const parsed = splitSiteUrl(raw);
				commit(parsed.protocol, parsed.host);
				return;
			}

			commit(protocol, raw);
		},
		[commit, protocol],
	);

	return (
		<Grid container spacing={2} alignItems="flex-start">
			<Grid item width={PROTOCOL_WIDTH}>
				<Select
					fullWidth
					value={protocol || SITE_PROTOCOLS.HTTPS}
					options={SITE_PROTOCOL_OPTIONS}
					onChange={handleProtocolChange}
					onBlur={() => setTouched(true, false)}
				/>
			</Grid>
			<Grid item xs>
				<TextField
					fullWidth
					name={name}
					value={host}
					placeholder={placeholder}
					onChange={handleHostChange}
					onBlur={field.onBlur}
					error={error}
				/>
			</Grid>
		</Grid>
	);
};

SiteUrlField.propTypes = {
	name: PropTypes.string.isRequired,
	placeholder: PropTypes.string,
};

export default SiteUrlField;
